/**
 * SOLY-PLAN-001 MS-34 — derives what the project page's planning block renders.
 *
 * | state        | conditions                                              | rendered      |
 * |--------------|---------------------------------------------------------|---------------|
 * | 'hidden'     | (a) feature flag off                                    | nothing       |
 * | 'picker'     | (b) flag on + planning initialized                      | order picker  |
 * | 'activation' | (c) flag on + not initialized + accepted client order   | empty state   |
 * | 'hidden'     | (d) flag on + not initialized + no accepted order       | nothing (*)   |
 *
 * (*) accepting a quote / confirming a direct sale auto-initializes planning server-side,
 * so an unsold project needs no activation gesture.
 *
 * `executionDetails === null` (no PED at all) reads as NOT initialized; an orphan PED
 * (execution-details dialog filled, no work items) carries isPlanningInitialized=false.
 * "Project active" is NOT derived here: the FE has no archived/cancelled signal on its DTOs —
 * the server guards it (409 PROJECT_INACTIVE) and the card surfaces the error toast.
 */
export type PlanningSectionState = 'hidden' | 'picker' | 'activation'

export interface PlanningSectionInputs {
  /** user.features.planning */
  planningEnabled: boolean
  /** The page's executionDetails state (null = no PED = not initialized). */
  executionDetails: { isPlanningInitialized: boolean } | null
  /**
   * True once the executionDetails fetch has settled without error. While false the
   * activation card is withheld: offering activation on an unknown flag would flash the
   * wrong state on an initialized project (slow load / fetch error). The picker is NOT
   * gated on this — a truthful loaded flag wins over an in-flight refetch.
   */
  executionDetailsSettled: boolean
  /** Accepted quote version or confirmed direct sale, derived from already-loaded page state. */
  hasAcceptedOrder: boolean
}

export function selectPlanningSectionState({
  planningEnabled,
  executionDetails,
  executionDetailsSettled,
  hasAcceptedOrder,
}: PlanningSectionInputs): PlanningSectionState {
  if (!planningEnabled) return 'hidden'
  if (executionDetails?.isPlanningInitialized === true) return 'picker'
  if (!executionDetailsSettled) return 'hidden'
  return hasAcceptedOrder ? 'activation' : 'hidden'
}
